import React, {ReactElement, useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ModelLintPanel from './ModelLint.component';
import TestReportMappingPanel from './TestReportMapping.component';
import ModelShakedownPanel from './ModelShakedown.component';
import {TestingScope} from './testing-scope';

/** Tab indices, exported so canvas actions can deep-link to a specific tool. */
export const MODEL_TESTING_TAB_REVIEW = 0;
export const MODEL_TESTING_TAB_TEST_REPORT = 1;
export const MODEL_TESTING_TAB_SHAKEDOWN = 2;

const MIN_HEIGHT = 200;

/**
 * "Model Testing" bottom dock: one home for the three model quality tools, cheapest
 * first: static Review (lint), single-report Test, and batch Shakedown. Docked at the
 * bottom of the canvas as a flex sibling, so the canvas reflows above it and the model
 * stays visible and editable while acting on findings, instead of a modal that covers
 * it. Panels stay mounted while the dock is open (hidden, not unmounted, when their tab
 * is inactive) so an in-flight run keeps polling while other tabs are viewed. Drag the
 * top edge to resize.
 */
export default function ModelTestingPanel(
    props: { open: boolean, scope: TestingScope | null, onClose: () => void, initialTab?: number },
): ReactElement | null {
    const {open, scope, onClose, initialTab} = props;
    const [tab, setTab] = useState<number>(initialTab ?? MODEL_TESTING_TAB_REVIEW);
    const [panelHeight, setPanelHeight] = useState<number>(() => Math.round(window.innerHeight * 0.42));

    useEffect((): void => {
        if (open) {
            setTab(initialTab ?? MODEL_TESTING_TAB_REVIEW);
        }
    }, [open, initialTab]);

    const handleResizeMouseDown = (event: React.MouseEvent): void => {
        event.preventDefault();
        const startY = event.clientY;
        const startHeight = panelHeight;
        const onMove = (moveEvent: MouseEvent): void => {
            const delta = startY - moveEvent.clientY;
            setPanelHeight(Math.min(Math.max(startHeight + delta, MIN_HEIGHT), window.innerHeight - 120));
        };
        const onUp = (): void => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    if (!open) {
        return null;
    }

    return (
        <div
            style={{
                height: panelHeight,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderTop: '1px solid #ccc',
                background: '#fff',
                overflow: 'hidden',
            }}
        >
            <div
                onMouseDown={handleResizeMouseDown}
                style={{height: 5, cursor: 'row-resize', background: 'transparent', flexShrink: 0}}
                onMouseOver={(e): void => {
                    (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,0,0,0.12)';
                }}
                onMouseOut={(e): void => {
                    (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                }}
            />
            <Box sx={{display: 'flex', alignItems: 'center', px: 2, flexShrink: 0}}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{mr: 3}}>Model Testing</Typography>
                <Tabs
                    value={tab}
                    onChange={(_, value: number): void => setTab(value)}
                    variant="scrollable"
                    sx={{flex: 1, minHeight: 0}}
                >
                    <Tab label="Review" />
                    <Tab label="Test Report" />
                    <Tab label="Shakedown" />
                </Tabs>
                <IconButton onClick={onClose} size="small" aria-label="Close model testing">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{flex: 1, overflow: 'auto', px: 2, pb: 1, borderTop: '1px solid', borderColor: 'divider'}}>
                <Box hidden={tab !== MODEL_TESTING_TAB_REVIEW} sx={{pt: 1}}>
                    <ModelLintPanel scope={scope} />
                </Box>
                <Box hidden={tab !== MODEL_TESTING_TAB_TEST_REPORT} sx={{pt: 1}}>
                    <TestReportMappingPanel scope={scope} />
                </Box>
                <Box hidden={tab !== MODEL_TESTING_TAB_SHAKEDOWN} sx={{pt: 1}}>
                    <ModelShakedownPanel scope={scope} />
                </Box>
            </Box>
        </div>
    );
}
